import type * as React from 'react';
import { forwardRef } from 'react';
import { cn } from '@/lib/utils';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  /** Rendered inside the field on the left — a mail or search glyph. */
  leading?: React.ReactNode;
  trailing?: React.ReactNode;
}

/**
 * forwardRef is load-bearing, not ceremony: a failed submit focuses the first
 * invalid control, and on React 18 that needs a ref reaching the real <input>.
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, leading, trailing, ...props },
  ref,
) {
  const field = (
    <input
      ref={ref}
      className={cn(
        'h-11 w-full rounded-[var(--radius-control)] border border-line-strong bg-surface px-3.5 text-base sm:text-[15px] text-ink',
        'placeholder:text-placeholder',
        'focus:border-brand focus:ring-4 focus:ring-brand/15 focus:outline-none',
        'disabled:bg-surface-2 disabled:cursor-not-allowed',
        'aria-[invalid=true]:border-danger aria-[invalid=true]:focus:ring-danger/15',
        leading && 'pl-11',
        trailing && 'pr-11',
        className,
      )}
      {...props}
    />
  );
  if (!leading && !trailing) return field;
  return (
    <div className="relative flex items-center">
      {leading && (
        <span className="pointer-events-none absolute left-3.5 grid place-items-center text-placeholder">
          {leading}
        </span>
      )}
      {field}
      {trailing && <span className="absolute right-2 grid place-items-center">{trailing}</span>}
    </div>
  );
});
